import { FC, useState } from 'react';
import ImageIconSolid from '@/components/icons/ImageIconSolid';
import PackageAddIcon from '@/components/icons/PackageAddIcon';
import { Button } from '@/components/ui/button';
import ApartmentsCardChart from '@/features/apartments/components/ApartmentsCard/ApartmentsCardChart';
import { GetApartmentListItemResponseDto } from '@/features/apartments/types/apartments-dto';
import EImageWithFallback from '@/components/everridge/EImageWithFallback';
import ApartmentsCardField from '@/features/apartments/components/ApartmentsCard/ApartmentsCardField';
import CalendarAddIcon from '@/components/icons/CalendarAddIcon';
import User_02_Icon from '@/components/icons/User_02_Icon';
import { cn } from '@/lib/utils';
import { t } from 'i18next';
import { Roles } from '@/@types/auth.types.ts';
import CurrencyValue from '@/features/finances/components/CurrencyValue';

type Props = {
	showDetails: (id: string) => void;
	setNewReservation?: (id: string) => void;
	setNewOrder?: (id: string, name?: string) => void;
	footerButtons?: Roles;
} & GetApartmentListItemResponseDto;

const ApartmentsCardMobile: FC<Props> = ({
	id,
	name,
	ownerName,
	description,
	cadastralCode,
	squareness,
	propertyBalance,
	otherServicesBalance,
	usedDays,
	maxDays,
	photoUrls,
	showDetails,
	setNewReservation,
	setNewOrder,
	footerButtons,
}) => {
	const [isPressed, setIsPressed] = useState(false);

	const ownerButtons = (
		<>
			<Button
				variant='outline'
				className='relative flex-1'
				onClick={() => setNewReservation?.(id)}>
				<CalendarAddIcon />
			</Button>
			<Button
				variant='outline'
				className='relative flex-1'
				onClick={() => setNewOrder?.(id, name)}>
				<PackageAddIcon />
			</Button>
		</>
	);

	const adminButtons = (
		<>
			<User_02_Icon className='shrink-0' />
			<span className='text-sm text-popover-1000 truncate'>
				{ownerName ??
					t('main.apartments.components.card.buttons.noOwner')}
			</span>
		</>
	);

	return (
		<div
			className={cn(
				'relative -mx-4 p-4 flex gap-4 bg-popover-10 hover:bg-popover-50 cursor-pointer',
				isPressed && 'bg-popover-100 transition-all duration-300 ease-in-out',
			)}>
			<div
				className='absolute inset-0 '
				onClick={() => showDetails(id)}
				onTouchStart={() => setIsPressed(true)}
				onTouchEnd={() => setIsPressed(false)}
			/>
			<EImageWithFallback
				src={photoUrls?.[0]}
				fallbackSVG={ImageIconSolid}
				containerClassName='flex-1 rounded-semi-xl overflow-hidden'
			/>
			<div className='flex-1 flex flex-col gap-4 truncate'>
				<div className='flex items-center justify-between gap-4'>
					<div className='truncate'>
						<h4 className='text-base text-popover-1000 font-medium'>
							{name}
						</h4>
						<p className='text-base text-popover-800 truncate'>
							{description ??
								t(
									'main.apartments.components.card.noDescription',
								)}
						</p>
					</div>
					<ApartmentsCardChart
						usedDays={usedDays}
						maxDays={maxDays}
					/>
				</div>
				<div className='flex flex-col gap-3'>
					<ApartmentsCardField
						property='Cadastral code'
						value={`${cadastralCode}`}
					/>
					<ApartmentsCardField
						property='Squareness'
						value={`${squareness}`}
					/>
					<ApartmentsCardField
						property='Property'
						value={<CurrencyValue value={propertyBalance} asset='property' />}
					/>
					<ApartmentsCardField
						property='OtherServices'
						value={<CurrencyValue value={otherServicesBalance} asset='otherservices' />}
					/>
					{!!footerButtons && (
						<div
							className={cn(
								'flex items-center',
								footerButtons === Roles.owner
									? 'gap-4'
									: 'gap-2.5',
							)}>
							{footerButtons === Roles.owner && ownerButtons}
							{footerButtons === Roles.admin && adminButtons}
						</div>
					)}
				</div>
			</div>
		</div>
	);
};

export default ApartmentsCardMobile;
